import { assessExecutableLegality } from '../skill-engine/executable-legality.js';
import { persistedSkillContext } from '../skill-engine/legality-context.js';
import { V13_MASTERY_MAX_LEVEL } from '../calibration/skill-damage-v13.js';
import type { CanonicalSkillV2Input } from '../skill-engine/types.js';
import { CANONICAL_SKILL_V3_SCHEMA_VERSION, type CanonicalSkillV3Input } from '../skill-engine/composite-types.js';
import { WEAPON_PROGRESSION_V0, CATALYST_WEAPON_IDS, FAMILY_REFERENCE_WEAPON_IDS, type FamilyReferenceWeaponId, type PlayableWeaponId } from '../reference/weapon-progression-v0.js';
import { WEAPON_ENHANCE_FIRST_PAID_MILESTONE, WEAPON_ENHANCE_TOKEN_LEVEL_INTERVAL, weaponEnhanceTokensPaidAt } from '../reference/weapon-enhance.js';
import { MIGRATION_CANONICAL_FORM, NewerStoreError, SAVED_SKILL_MIGRATIONS } from './saved-skill-migrations.js';
import { PRIMARY_STAT_KEYS, type PrimaryStatKey, type PrimaryStats } from '../sim/primary-stats.js';

export interface SavedSkill {
  id: string;
  name: string;
  /** A composite definition is the loader's own verified/compiled
   * `CanonicalSkillV3` (`loadCanonicalSkill`'s `READY_COMPOSITE.plan
   * .canonicalSkill`), never the raw unvalidated authored input — matching
   * how the V2 branch already stores `loaded.skill`, not `record.definition`. */
  definition: CanonicalSkillV2Input | CanonicalSkillV3Input;
  /** 1..10; rises with the skill's Mastery XP (`skill-mastery.ts`). */
  masteryLevel: number;
  /** Mastery XP earned toward the next level; omitted means none. */
  masteryXp?: number;
  cooldownRemainingS: number;
  chargesRemaining?: number;
  chargeCarryS?: number;
  /** The ids of the stored-skill rewrites this record has had (`saved-skill-migrations.ts`); absent on a record written before they existed. */
  migrations?: string[];
  /** The pricing model the stored cooldown and mana were computed under; absent means `LEGACY_PRICING_MODEL_ID` (`pricing-model.ts`). */
  pricing?: SkillPricingStamp;
  /** Set when pricing this skill again under `model` failed: it keeps its price and is not tried again until the model id changes. */
  pricingFailure?: SkillPricingFailure;
}

export interface SkillPricingStamp { model: string; at: string }
export interface SkillPricingFailure { model: string; reason: string; at: string }

/**
 * A saved skill the current engine cannot execute. It is kept, not dropped: `originalDefinition` is the
 * definition exactly as the store held it (a JSON value that nothing compiles, owns, prices or walks except
 * the boot re-check), so a later engine that accepts it again gives the skill back.
 */
export interface SkillQuarantine {
  /** The loader's refusal code (or a stable code of the boot step that failed). */
  code: string;
  message: string;
  /** ISO time of the boot that quarantined it. */
  at: string;
  originalDefinition: unknown;
}

/** The envelope of a saved skill the engine cannot execute: it is never equipped, cast, priced or summarized. */
export interface QuarantinedSkill {
  id: string;
  name: string;
  masteryLevel: number;
  masteryXp?: number;
  cooldownRemainingS: number;
  chargesRemaining?: number;
  chargeCarryS?: number;
  /** The rewrites this record has had; it keeps the ones it lacks until the engine accepts it again. */
  migrations?: string[];
  pricing?: SkillPricingStamp;
  pricingFailure?: SkillPricingFailure;
  quarantine: SkillQuarantine;
}

export interface CharacterRecord {
  version: 1;
  revision: number;
  level: number;
  xp: number;
  unspentPoints: number;
  stats: PrimaryStats;
  allocationHistory: PrimaryStatKey[];
  weaponId: PlayableWeaponId;
  skills: SavedSkill[];
  /** Saved skills the current engine refuses; they count toward `MAX_SAVED_SKILLS` and own their ids. */
  quarantinedSkills: QuarantinedSkill[];
  equippedSkillIds: (string | null)[];
  down: boolean;
  deathSerial: number;
  /** True once any dev-tool mutation touched this character; such characters are excluded from play measurements. */
  debugApplied: boolean;
  /** PROVISIONAL placeholder currency: one spends a token to refund every allocated point. */
  respecTokens: number;
  /** PROVISIONAL: the highest level-10 milestone already paid out, so dying below a milestone and re-reaching it grants nothing. */
  respecMilestone: number;
  /** PROVISIONAL placeholder currency (무기 강화권): one spends a token to raise `weaponEnhanceStep` by one. */
  weaponEnhanceTokens: number;
  /** PROVISIONAL: the highest level-10 milestone already counted for weapon tokens, so dying below a milestone and re-reaching it grants nothing. */
  weaponEnhanceMilestone: number;
  /** PROVISIONAL: spent enhancement steps; belongs to the character, so changing weapon keeps it. Step 0 is the unenhanced weapon. */
  weaponEnhanceStep: number;
  /** Operations setting, not balance: this character's own multiplier on the XP its kills grant, on top of the server-wide rate. 1 unless a dev tool set it. */
  xpRate: number;
}

/** PROVISIONAL placeholder economy: a new character starts with this many 재분배권. */
export const RESPEC_TOKENS_START = 1;
/** PROVISIONAL placeholder economy: one more token each time this many levels are first reached. */
export const RESPEC_TOKEN_LEVEL_INTERVAL = 10;
export { WEAPON_ENHANCE_FIRST_PAID_MILESTONE, WEAPON_ENHANCE_TOKEN_LEVEL_INTERVAL, weaponEnhanceTokensPaidAt };

export const MAX_SAVED_SKILLS = 32;
export const MAX_SKILL_ID_LENGTH = 128;
export const MAX_SKILL_NAME_LENGTH = 128;
const BALANCE_WEAPON_IDS: readonly PlayableWeaponId[] = Object.freeze(
  (Object.keys(WEAPON_PROGRESSION_V0) as (PlayableWeaponId | FamilyReferenceWeaponId)[])
    .filter((id): id is PlayableWeaponId => !(FAMILY_REFERENCE_WEAPON_IDS as readonly string[]).includes(id)),
);
export const PLAYABLE_WEAPON_IDS: readonly PlayableWeaponId[] = Object.freeze([...BALANCE_WEAPON_IDS, ...CATALYST_WEAPON_IDS]);

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);
const isSafeInteger = (value: unknown): value is number => typeof value === 'number' && Number.isSafeInteger(value);
const isFiniteNonNegative = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value >= 0;
const cloneJson = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
/** The character record (or one of its envelopes) is malformed: a user error on a write, corruption at boot. */
export class CharacterValidationError extends Error {
  constructor(message: string) { super(`Invalid character: ${message}`); this.name = 'CharacterValidationError'; }
}
const reject = (message: string): never => { throw new CharacterValidationError(message); };

function keysExactly(value: Record<string, unknown>, keys: readonly string[], path: string): void {
  for (const key of Object.keys(value)) if (!keys.includes(key)) reject(`${path}.${key} is unknown`);
}

function validateStats(value: unknown): PrimaryStats {
  if (!isRecord(value)) reject('stats must be an object');
  const record = value as Record<string, unknown>;
  keysExactly(record, PRIMARY_STAT_KEYS, 'stats');
  const stats = {} as PrimaryStats;
  for (const key of PRIMARY_STAT_KEYS) {
    if (!isSafeInteger(record[key]) || record[key] < 0) reject(`stats.${key} must be a non-negative safe integer`);
    stats[key] = record[key] as number;
  }
  return stats;
}

/** Rebuilds a skill tree, handing each effect node to `fill` to state a missing field. */
export function fillEffectNodes<T>(node: T, fill: (effect: Record<string, unknown>) => void): T {
  if (Array.isArray(node)) return node.map((child) => fillEffectNodes(child, fill)) as T;
  if (!isRecord(node)) return node;
  const next: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(node)) {
    // Authoring evidence describes the solve; it is not part of the executable effect tree.
    next[key] = key === 'authoringProvenance' || key === 'nonDamageAuthoringProvenance'
      ? value : fillEffectNodes(value, fill);
  }
  fill(next);
  return next as T;
}

/**
 * N4: a knockback with no stated origin runs from the attack unit's anchor, a
 * pull to the caster. Every skill is saved with those defaults written out
 * (`validateSavedSkill` is the gate each saved skill passes), so that what is
 * stored is what runs and a later change of default cannot move it. Only
 * displacement effects carry distanceM; authoring provenance shares their kind.
 */
export function withExplicitDisplacementOrigins<T>(definition: T): T {
  return fillEffectNodes(definition, (effect) => {
    if (effect.kind === 'knockback' && typeof effect.distanceM === 'number' && effect.from === undefined) effect.from = 'anchor';
    if (effect.kind === 'pull' && typeof effect.distanceM === 'number' && effect.to === undefined) effect.to = 'me';
  });
}

/**
 * Saves written before `from` existed pushed away from the caster. Applied once,
 * to the raw store as it is read from disk, before anything is validated: a
 * knockback still lacking `from` there is genuinely old, because everything
 * saved since states it.
 */
export function pinLegacyKnockbackOrigins<T>(rawStore: T): T {
  return fillEffectNodes(rawStore, (effect) => {
    if (effect.kind === 'knockback' && typeof effect.distanceM === 'number' && effect.from === undefined) effect.from = 'me';
  });
}

const ENVELOPE_KEYS = ['id', 'name', 'masteryLevel', 'masteryXp', 'cooldownRemainingS', 'chargesRemaining', 'chargeCarryS', 'migrations', 'pricing', 'pricingFailure'] as const;

/** The fields every saved skill carries, executable or not; a malformed one is corruption, never a quarantine. */
export function validateSkillEnvelope(value: unknown, path: string, extraKeys: readonly string[]): Omit<QuarantinedSkill, 'quarantine'> {
  if (!isRecord(value)) reject(`${path} must be an object`);
  const record = value as Record<string, unknown>;
  keysExactly(record, [...ENVELOPE_KEYS, ...extraKeys], path);
  if (typeof record.id !== 'string' || record.id.length === 0 || record.id.length > MAX_SKILL_ID_LENGTH) reject(`${path}.id is invalid`);
  if (typeof record.name !== 'string' || record.name.length === 0 || record.name.length > MAX_SKILL_NAME_LENGTH) reject(`${path}.name is invalid`);
  if (!isSafeInteger(record.masteryLevel) || (record.masteryLevel as number) < 1 || (record.masteryLevel as number) > V13_MASTERY_MAX_LEVEL) reject(`${path}.masteryLevel must be an integer from 1 to ${V13_MASTERY_MAX_LEVEL}`);
  if (record.masteryXp !== undefined && !isFiniteNonNegative(record.masteryXp)) reject(`${path}.masteryXp is invalid`);
  if (!isFiniteNonNegative(record.cooldownRemainingS)) reject(`${path}.cooldownRemainingS is invalid`);
  if (record.chargesRemaining !== undefined && (!isSafeInteger(record.chargesRemaining) || record.chargesRemaining < 0)) reject(`${path}.chargesRemaining is invalid`);
  if (record.chargeCarryS !== undefined && !isFiniteNonNegative(record.chargeCarryS)) reject(`${path}.chargeCarryS is invalid`);
  if (record.migrations !== undefined && (!Array.isArray(record.migrations) || record.migrations.some((id) => typeof id !== 'string') || new Set(record.migrations).size !== record.migrations.length)) reject(`${path}.migrations is invalid`);
  const stringFields = (value: unknown, keys: readonly string[]): value is Record<string, string> => isRecord(value) && Object.keys(value).length === keys.length && keys.every((key) => typeof value[key] === 'string');
  if (record.pricing !== undefined && !stringFields(record.pricing, ['model', 'at'])) reject(`${path}.pricing is invalid`);
  if (record.pricingFailure !== undefined && !stringFields(record.pricingFailure, ['model', 'reason', 'at'])) reject(`${path}.pricingFailure is invalid`);
  const unknown = (record.migrations as string[] | undefined)?.find((id) => !SAVED_SKILL_MIGRATIONS.includes(id));
  if (unknown !== undefined) throw new NewerStoreError(`${path} carries the migration "${unknown}", which this build does not know: the store was written by a newer build. Restore the backup taken before the upgrade, or run the newer build`);
  return {
    id: record.id as string,
    name: record.name as string,
    masteryLevel: record.masteryLevel as number,
    ...(record.masteryXp === undefined || record.masteryXp === 0 ? {} : { masteryXp: record.masteryXp as number }),
    cooldownRemainingS: record.cooldownRemainingS as number,
    ...(record.chargesRemaining === undefined ? {} : { chargesRemaining: record.chargesRemaining as number }),
    ...(record.chargeCarryS === undefined ? {} : { chargeCarryS: record.chargeCarryS as number }),
    ...(record.migrations === undefined ? {} : { migrations: [...(record.migrations as string[])] }),
    ...(record.pricing === undefined ? {} : { pricing: { ...(record.pricing as SkillPricingStamp) } }),
    ...(record.pricingFailure === undefined ? {} : { pricingFailure: { ...(record.pricingFailure as SkillPricingFailure) } }),
  };
}

export function validateQuarantinedSkill(value: unknown, index: number): QuarantinedSkill {
  const path = `quarantinedSkills[${index}]`;
  const envelope = validateSkillEnvelope(value, path, ['quarantine']);
  const quarantine = (value as Record<string, unknown>).quarantine;
  if (!isRecord(quarantine)) return reject(`${path}.quarantine must be an object`);
  keysExactly(quarantine, ['code', 'message', 'at', 'originalDefinition'], `${path}.quarantine`);
  if (typeof quarantine.code !== 'string' || typeof quarantine.message !== 'string' || typeof quarantine.at !== 'string' || !('originalDefinition' in quarantine)) reject(`${path}.quarantine is invalid`);
  return { ...envelope, quarantine: { code: quarantine.code as string, message: quarantine.message as string, at: quarantine.at as string, originalDefinition: cloneJson(quarantine.originalDefinition) } };
}

function validateSavedSkill(value: unknown, index: number): SavedSkill {
  const envelope = validateSkillEnvelope(value, `skills[${index}]`, ['definition']);
  const record = value as Record<string, unknown>;
  // A definition the loader throws on is as unexecutable as one it refuses: a user error here, not a storage fault.
  let loaded: ReturnType<typeof assessExecutableLegality>;
  try { loaded = assessExecutableLegality(record.definition, persistedSkillContext()); }
  catch { return reject(`skills[${index}].definition is not executable canonical skill (THROWN)`); }
  if (loaded.status === 'INVALID' || loaded.status === 'DEFERRED') reject(`skills[${index}].definition is not executable canonical skill (${loaded.status})`);
  // A composite loads as READY_COMPOSITE, which carries `plan` and no
  // `skill`. Reading `.skill` off it stored `undefined` as the definition —
  // the record validated, the write succeeded, and the failure surfaced two
  // sessions later as unparseable JSON with nothing pointing back here.
  // Storing `loaded.plan.canonicalSkill` (the real validated+compiled V3,
  // mirroring the V2 branch's own `loaded.skill`) instead of the raw
  // `record.definition` fixes that: the loader has already accepted it, and
  // this is the same skill the player owns, not a re-derivation of it.
  //
  // A degenerate V3 (one plain attack) loads READY, lowered to V2 for
  // execution. That lowering is the loader's execution view, not the skill:
  // the V3 the player owns is stored as given (the loader has just accepted
  // it), so persistence never re-writes a V3 as V2 (P10 single-write).
  // Legacy V1/V2 saves keep storing the loader's canonical V2, as before.
  const definition = record.definition as CanonicalSkillV2Input | CanonicalSkillV3Input;
  // A record that has been through the canonical-form rewrite is stored as it runs; only the loader's check is repeated.
  if (envelope.migrations?.includes(MIGRATION_CANONICAL_FORM)) return { ...envelope, migrations: [...SAVED_SKILL_MIGRATIONS], definition: cloneJson(definition) };
  const canonical: CanonicalSkillV2Input | CanonicalSkillV3Input = loaded.status === 'READY_COMPOSITE'
    ? loaded.plan.canonicalSkill
    : definition.schemaVersion === CANONICAL_SKILL_V3_SCHEMA_VERSION
      ? definition
      : (loaded as { status: 'READY'; skill: CanonicalSkillV2Input }).skill;
  return { ...envelope, migrations: [...SAVED_SKILL_MIGRATIONS], definition: withExplicitDisplacementOrigins(cloneJson(canonical)) };
}

/** Validate and deep-clone persisted owner data at the canonical boundary. */
export function validateCharacter(value: unknown): CharacterRecord {
  if (!isRecord(value)) reject('record must be an object');
  const record = value as Record<string, unknown>;
  keysExactly(record, ['version', 'revision', 'level', 'xp', 'unspentPoints', 'stats', 'allocationHistory', 'weaponId', 'skills', 'quarantinedSkills', 'equippedSkillIds', 'down', 'deathSerial', 'debugApplied', 'respecTokens', 'respecMilestone', 'weaponEnhanceTokens', 'weaponEnhanceMilestone', 'weaponEnhanceStep', 'xpRate'], 'character');
  if (record.version !== 1) reject('version must be 1');
  if (!isSafeInteger(record.revision) || record.revision < 0) reject('revision is invalid');
  if (!isSafeInteger(record.level) || record.level < 1 || record.level > 100) reject('level is invalid');
  if (!isSafeInteger(record.xp) || record.xp < 0 || record.xp >= xpToNextLevel(record.level as number)) reject('xp is invalid');
  if (!isSafeInteger(record.unspentPoints) || record.unspentPoints < 0) reject('unspentPoints is invalid');
  const revision = record.revision as number;
  const level = record.level as number;
  const xp = record.xp as number;
  const unspentPoints = record.unspentPoints as number;
  const stats = validateStats(record.stats);
  if (!Array.isArray(record.allocationHistory) || record.allocationHistory.some((key) => !PRIMARY_STAT_KEYS.includes(key as PrimaryStatKey))) reject('allocationHistory is invalid');
  const history = [...(record.allocationHistory as unknown[])] as PrimaryStatKey[];
  const statTotal = PRIMARY_STAT_KEYS.reduce((sum, key) => sum + stats[key], 0);
  if (history.length !== statTotal) reject('allocationHistory does not match stats');
  const historyCounts = Object.fromEntries(PRIMARY_STAT_KEYS.map((key) => [key, 0])) as Record<PrimaryStatKey, number>;
  for (const key of history) historyCounts[key] += 1;
  for (const key of PRIMARY_STAT_KEYS) if (historyCounts[key] !== stats[key]) reject('allocationHistory order/count does not match stats');
  if (statTotal + unspentPoints !== level * 5) reject('point budget is invalid');
  if (typeof record.weaponId !== 'string' || !PLAYABLE_WEAPON_IDS.includes(record.weaponId as PlayableWeaponId)) reject('weaponId is invalid');
  if (!Array.isArray(record.skills) || record.skills.length > MAX_SAVED_SKILLS) reject('skills is invalid');
  const skills = (record.skills as unknown[]).map((skill, index) => validateSavedSkill(skill, index));
  // Saves written before quarantine existed carry none.
  if (record.quarantinedSkills !== undefined && !Array.isArray(record.quarantinedSkills)) reject('quarantinedSkills is invalid');
  const quarantinedSkills = ((record.quarantinedSkills as unknown[] | undefined) ?? []).map((skill, index) => validateQuarantinedSkill(skill, index));
  if (skills.length + quarantinedSkills.length > MAX_SAVED_SKILLS) reject('skills is invalid');
  const skillIds = new Set<string>();
  for (const skill of skills) { if (skillIds.has(skill.id)) reject('skill ids must be unique'); skillIds.add(skill.id); }
  for (const skill of quarantinedSkills) { if (skillIds.has(skill.id)) reject('skill ids must be unique'); skillIds.add(skill.id); }
  if (!Array.isArray(record.equippedSkillIds) || record.equippedSkillIds.length !== 4) reject('equippedSkillIds must contain four slots');
  const equipped = (record.equippedSkillIds as unknown[]).map((id): string | null => {
    if (id !== null && typeof id !== 'string') reject('equipped skill id must be string or null');
    return id as string | null;
  });
  const equippedIds = equipped.filter((id): id is string => id !== null);
  if (new Set(equippedIds).size !== equippedIds.length) reject('equipped skill ids must be unique');
  if (equippedIds.some((id) => !skillIds.has(id))) reject('equipped skill must be owned');
  if (quarantinedSkills.some((skill) => equippedIds.includes(skill.id))) reject('a quarantined skill cannot be equipped');
  if (typeof record.down !== 'boolean') reject('down must be boolean');
  if (!isSafeInteger(record.deathSerial) || record.deathSerial < 0) reject('deathSerial is invalid');
  // Saves written before these fields existed omit them. A missing flag means no dev tool was ever applied;
  // missing token counts are what the placeholder economy would have granted by this level (PROVISIONAL);
  // a missing XP rate is 1.
  const earnedMilestone = Math.floor(level / RESPEC_TOKEN_LEVEL_INTERVAL);
  if (record.debugApplied !== undefined && typeof record.debugApplied !== 'boolean') reject('debugApplied must be boolean');
  if (record.respecTokens !== undefined && (!isSafeInteger(record.respecTokens) || record.respecTokens < 0)) reject('respecTokens is invalid');
  if (record.respecMilestone !== undefined && (!isSafeInteger(record.respecMilestone) || record.respecMilestone < 0)) reject('respecMilestone is invalid');
  if (record.weaponEnhanceTokens !== undefined && (!isSafeInteger(record.weaponEnhanceTokens) || record.weaponEnhanceTokens < 0)) reject('weaponEnhanceTokens is invalid');
  if (record.weaponEnhanceMilestone !== undefined && (!isSafeInteger(record.weaponEnhanceMilestone) || record.weaponEnhanceMilestone < 0)) reject('weaponEnhanceMilestone is invalid');
  if (record.weaponEnhanceStep !== undefined && (!isSafeInteger(record.weaponEnhanceStep) || record.weaponEnhanceStep < 0)) reject('weaponEnhanceStep is invalid');
  if (record.xpRate !== undefined && (!isFiniteNonNegative(record.xpRate) || record.xpRate === 0)) reject('xpRate is invalid');
  const respecMilestone = (record.respecMilestone as number | undefined) ?? earnedMilestone;
  // A save without the weapon fields starts from the respec high-water mark (not floor(level/10)), so a character that
  // died below a milestone is not paid for it twice; the mark then pays only what the Lv30 start allows.
  const weaponEnhanceMilestone = (record.weaponEnhanceMilestone as number | undefined) ?? respecMilestone;
  return {
    version: 1, revision, level, xp, unspentPoints, stats, allocationHistory: history, weaponId: record.weaponId as PlayableWeaponId, skills, quarantinedSkills, equippedSkillIds: equipped, down: record.down as boolean, deathSerial: record.deathSerial as number,
    debugApplied: (record.debugApplied as boolean | undefined) ?? false,
    respecTokens: (record.respecTokens as number | undefined) ?? RESPEC_TOKENS_START + earnedMilestone,
    respecMilestone,
    weaponEnhanceTokens: (record.weaponEnhanceTokens as number | undefined) ?? weaponEnhanceTokensPaidAt(weaponEnhanceMilestone),
    weaponEnhanceMilestone,
    weaponEnhanceStep: (record.weaponEnhanceStep as number | undefined) ?? 0,
    xpRate: (record.xpRate as number | undefined) ?? 1,
  };
}

/** An independent copy of a record that has already passed `validateCharacter`; validation belongs to the boot read and to writes accepted from a request. */
export function cloneCharacter(character: CharacterRecord): CharacterRecord {
  return structuredClone(character);
}

// Kept here for validation's runtime use; progression.ts re-exports the public rule.
export function xpToNextLevel(level: number): number {
  if (!isSafeInteger(level) || level < 1 || level > 100) throw new Error(`level must be between 1 and 100 (got ${level})`);
  return 100 + 25 * (level - 1) ** 2;
}

export function newCharacterRecord(): CharacterRecord {
  return { version: 1, revision: 0, level: 1, xp: 0, unspentPoints: 5, stats: { STR: 0, AGI: 0, TEC: 0, VIT: 0, MAG: 0, WIL: 0 }, allocationHistory: [], weaponId: 'SWORD', skills: [], quarantinedSkills: [], equippedSkillIds: [null, null, null, null], down: false, deathSerial: 0, debugApplied: false, respecTokens: RESPEC_TOKENS_START, respecMilestone: 0, weaponEnhanceTokens: 0, weaponEnhanceMilestone: 0, weaponEnhanceStep: 0, xpRate: 1 };
}
